import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { Conversation, Message } from "@/data/communicationTypes";
import { toUuidOrNull } from "@/lib/uuid";
import { REALTIME_ROW_PATCHING_ENABLED, SUPABASE_SYNC_DEBUG } from "@/lib/dataRuntimeFlags";
import { recordRealtimeMetric } from "@/lib/queryObservability";

function mapDbConversationToConversation(dbConv: Record<string, unknown>): Conversation {
  return {
    id: dbConv.id as string,
    contactName: dbConv.contact_name as string,
    contactPhone: dbConv.contact_phone as string,
    contactEmail: dbConv.contact_email as string,
    linkedLeadId: dbConv.linked_lead_id as string | undefined,
    linkedQuoteId: dbConv.linked_quote_id as string | undefined,
    linkedJobId: dbConv.linked_job_id as string | undefined,
    assignedRep: dbConv.assigned_rep as string,
    createdAt: dbConv.created_at as string,
    updatedAt: dbConv.updated_at as string,
    lastMessagePreview: dbConv.last_message_preview as string,
    unreadCount: dbConv.unread_count as number,
  };
}

function mapDbMessageToMessage(dbMsg: Record<string, unknown>): Message {
  return {
    id: dbMsg.id as string,
    conversationId: dbMsg.conversation_id as string,
    direction: dbMsg.direction as Message['direction'],
    channel: dbMsg.channel as Message['channel'],
    body: dbMsg.body as string,
    sender: dbMsg.sender as string,
    timestamp: dbMsg.timestamp as string,
    deliveryStatus: dbMsg.delivery_status as Message['deliveryStatus'],
    callDuration: dbMsg.call_duration as number | undefined,
    callOutcome: dbMsg.call_outcome as Message['callOutcome'],
    tag: dbMsg.tag as string | undefined,
  };
}

export const ConversationService = {
  async fetchConversations(): Promise<Conversation[]> {
    try {
      const { data, error } = await supabase
        .from("conversations")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.warn("Error fetching conversations from Supabase:", error.message);
        return [];
      }

      if (!data) return [];
      return data.map(mapDbConversationToConversation);
    } catch (err) {
      console.warn("Exception fetching conversations:", err);
      return [];
    }
  },

  async fetchConversationById(id: string): Promise<Conversation | null> {
    try {
      const { data, error } = await supabase
        .from("conversations")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        console.warn(`Error fetching conversation ${id}:`, error.message);
        return null;
      }

      return data ? mapDbConversationToConversation(data) : null;
    } catch (err) {
      console.warn(`Exception fetching conversation ${id}:`, err);
      return null;
    }
  },

  async createConversation(conv: Partial<Conversation>): Promise<Conversation | null> {
    try {
      let organizationId: string | null = null;
      try { organizationId = await getCurrentOrganizationId(); } catch { /* proceed without */ }

      const dbConv: Record<string, unknown> = {
        contact_name: conv.contactName ?? '',
        contact_phone: conv.contactPhone ?? '',
        contact_email: conv.contactEmail ?? '',
        linked_lead_id: conv.linkedLeadId ?? null,
        linked_quote_id: conv.linkedQuoteId ?? null,
        linked_job_id: conv.linkedJobId ?? null,
        assigned_rep: toUuidOrNull(conv.assignedRep) ?? '',
        last_message_preview: conv.lastMessagePreview ?? '',
        unread_count: conv.unreadCount ?? 0,
        organization_id: organizationId,
      };

      const { data, error } = await supabase
        .from("conversations")
        .insert(dbConv)
        .select()
        .single();

      if (error) {
        console.warn("Error creating conversation:", error.message);
        return null;
      }

      return data ? mapDbConversationToConversation(data) : null;
    } catch (err) {
      console.warn("Exception creating conversation:", err);
      return null;
    }
  },

  async updateConversation(id: string, updates: Partial<Conversation>): Promise<Conversation | null> {
    try {
      const dbConv: Record<string, unknown> = {};

      if (updates.contactName !== undefined) dbConv.contact_name = updates.contactName;
      if (updates.contactPhone !== undefined) dbConv.contact_phone = updates.contactPhone;
      if (updates.contactEmail !== undefined) dbConv.contact_email = updates.contactEmail;
      if (updates.linkedLeadId !== undefined) dbConv.linked_lead_id = updates.linkedLeadId;
      if (updates.linkedQuoteId !== undefined) dbConv.linked_quote_id = updates.linkedQuoteId;
      if (updates.linkedJobId !== undefined) dbConv.linked_job_id = updates.linkedJobId;
      if (updates.assignedRep !== undefined) dbConv.assigned_rep = toUuidOrNull(updates.assignedRep);
      if (updates.lastMessagePreview !== undefined) dbConv.last_message_preview = updates.lastMessagePreview;
      if (updates.unreadCount !== undefined) dbConv.unread_count = updates.unreadCount;

      const { data, error } = await supabase
        .from("conversations")
        .update(dbConv)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.warn(`Error updating conversation ${id}:`, error.message);
        return null;
      }

      return data ? mapDbConversationToConversation(data) : null;
    } catch (err) {
      console.warn(`Exception updating conversation ${id}:`, err);
      return null;
    }
  },

  async fetchMessages(conversationId: string): Promise<Message[]> {
    try {
      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("timestamp", { ascending: true });

      if (error) {
        console.warn(`Error fetching messages for conversation ${conversationId}:`, error.message);
        return [];
      }

      if (!data) return [];
      return data.map(mapDbMessageToMessage);
    } catch (err) {
      console.warn(`Exception fetching messages for conversation ${conversationId}:`, err);
      return [];
    }
  },

  async fetchConversationsByLeadIds(leadIds: string[]): Promise<Conversation[]> {
    if (leadIds.length === 0) return [];
    
    try {
      const { data, error } = await supabase
        .from("conversations")
        .select("*")
        .in("linked_lead_id", leadIds)
        .order("created_at", { ascending: false });

      if (error) {
        console.warn("Error fetching conversations by lead IDs:", error.message);
        return [];
      }

      if (!data) return [];
      return data.map(mapDbConversationToConversation);
    } catch (err) {
      console.warn("Exception fetching conversations by lead IDs:", err);
      return [];
    }
  },

  async fetchMessagesByConversationIds(conversationIds: string[]): Promise<Message[]> {
    if (conversationIds.length === 0) return [];
    
    try {
      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .in("conversation_id", conversationIds)
        .order("timestamp", { ascending: true });

      if (error) {
        console.warn("Error fetching messages by conversation IDs:", error.message);
        return [];
      }

      if (!data) return [];
      return data.map(mapDbMessageToMessage);
    } catch (err) {
      console.warn("Exception fetching messages by conversation IDs:", err);
      return [];
    }
  },

  async createMessage(msg: Partial<Message>): Promise<Message | null> {
    try {
      const dbMsg: Record<string, unknown> = {
        id: msg.id,
        conversation_id: msg.conversationId,
        direction: msg.direction,
        channel: msg.channel,
        body: msg.body,
        sender: msg.sender,
        timestamp: msg.timestamp,
        delivery_status: msg.deliveryStatus,
        call_duration: msg.callDuration,
        call_outcome: msg.callOutcome,
        tag: msg.tag,
      };

      const { data, error } = await supabase
        .from("messages")
        .insert(dbMsg)
        .select()
        .single();

      if (error) {
        console.warn("Error creating message:", error.message);
        return null;
      }

      return data ? mapDbMessageToMessage(data) : null;
    } catch (err) {
      console.warn("Exception creating message:", err);
      return null;
    }
  },

  subscribeToConversations(callback: (conversations: Conversation[]) => void): () => void {
    try {
      let disposed = false;
      let timer: ReturnType<typeof setTimeout> | null = null;
      let inFlight = false;
      let snapshot: Conversation[] | null = null;

      const refresh = async () => {
        if (disposed || inFlight) return;
        inFlight = true;
        try {
          recordRealtimeMetric({ source: "conversation-service", event: "full-refetch", table: "conversations" });
          const conversations = await ConversationService.fetchConversations();
          if (!disposed) {
            snapshot = conversations;
            callback(conversations);
          }
        } finally {
          inFlight = false;
        }
      };

      const scheduleRefresh = () => {
        if (disposed) return;
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          void refresh();
        }, 250);
      };

      const applyRealtimePatch = async (payload: { eventType?: string; new?: Record<string, unknown>; old?: Record<string, unknown> }) => {
        if (!REALTIME_ROW_PATCHING_ENABLED || !snapshot) {
          scheduleRefresh();
          return;
        }

        const eventType = String(payload.eventType ?? "").toUpperCase();
        const entityId = String((payload.new?.id ?? payload.old?.id ?? "") || "");
        if (!entityId) {
          scheduleRefresh();
          return;
        }

        if (eventType === "DELETE") {
          snapshot = snapshot.filter((conversation) => conversation.id !== entityId);
        } else if (payload.new) {
          const next = mapDbConversationToConversation(payload.new);
          const exists = snapshot.some((conversation) => conversation.id === next.id);
          snapshot = exists
            ? snapshot.map((conversation) => conversation.id === next.id ? next : conversation)
            : [next, ...snapshot];
          snapshot = [...snapshot].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        } else {
          scheduleRefresh();
          return;
        }

        recordRealtimeMetric({
          source: "conversation-service",
          event: "patch",
          table: "conversations",
          entityId,
          detail: SUPABASE_SYNC_DEBUG ? { eventType } : undefined,
        });
        if (!disposed) callback(snapshot);
      };

      const channel = supabase
        .channel('conversations-changes')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'conversations' },
          (payload) => { void applyRealtimePatch(payload as any); }
        )
        .subscribe();

      return () => {
        disposed = true;
        if (timer) clearTimeout(timer);
        supabase.removeChannel(channel);
      };
    } catch (err) {
      console.warn("Exception subscribing to conversations:", err);
      return () => {};
    }
  },

  subscribeToMessages(callback: (messages: Message[]) => void, conversationId: string): () => void {
    try {
      let disposed = false;
      let timer: ReturnType<typeof setTimeout> | null = null;
      let inFlight = false;
      let snapshot: Message[] | null = null;

      const refresh = async () => {
        if (disposed || inFlight) return;
        inFlight = true;
        try {
          recordRealtimeMetric({ source: "conversation-service", event: "full-refetch", table: "messages", entityId: conversationId });
          const messages = await ConversationService.fetchMessages(conversationId);
          if (!disposed) {
            snapshot = messages;
            callback(messages);
          }
        } finally {
          inFlight = false;
        }
      };

      const scheduleRefresh = () => {
        if (disposed) return;
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          void refresh();
        }, 250);
      };

      const applyRealtimePatch = (payload: { eventType?: string; new?: Record<string, unknown>; old?: Record<string, unknown> }) => {
        if (!REALTIME_ROW_PATCHING_ENABLED || !snapshot) {
          scheduleRefresh();
          return;
        }

        const eventType = String(payload.eventType ?? "").toUpperCase();
        const entityId = String((payload.new?.id ?? payload.old?.id ?? "") || "");
        if (!entityId) {
          scheduleRefresh();
          return;
        }

        if (eventType === "DELETE") {
          snapshot = snapshot.filter((message) => message.id !== entityId);
        } else if (payload.new) {
          const next = mapDbMessageToMessage(payload.new);
          const exists = snapshot.some((message) => message.id === next.id);
          snapshot = exists
            ? snapshot.map((message) => message.id === next.id ? next : message)
            : [...snapshot, next];
          snapshot = [...snapshot].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
        } else {
          scheduleRefresh();
          return;
        }

        recordRealtimeMetric({
          source: "conversation-service",
          event: "patch",
          table: "messages",
          entityId,
          detail: SUPABASE_SYNC_DEBUG ? { eventType, conversationId } : undefined,
        });
        if (!disposed) callback(snapshot);
      };

      const channel = supabase
        .channel(`messages-changes-${conversationId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
          (payload) => applyRealtimePatch(payload as any)
        )
        .subscribe();

      return () => {
        disposed = true;
        if (timer) clearTimeout(timer);
        supabase.removeChannel(channel);
      };
    } catch (err) {
      console.warn(`Exception subscribing to messages for ${conversationId}:`, err);
      return () => {};
    }
  },
};
